/**
 * lib/search-as-you-type.ts — when a field that searches as the listener types asks the server.
 *
 * Task 10 wrote these rules for «Поиск» (`store/search.ts`); task 76 gave the field over «Видео»
 * the same ones (`store/video.ts`), so they live here once and the two cannot drift apart. The
 * store keeps what is drawn; this keeps what is not — the timer, the numbers, the query on its
 * way.
 *
 * ## One request per pause in typing
 *
 * A keystroke only updates the field. The request goes {@link SEARCH_DEBOUNCE_MS} after the
 * LAST keystroke, so typing «sizzla» at a normal pace is one request, not six. The field
 * answers the finger at once; only the list below waits.
 *
 * ## Only the newest answer is drawn
 *
 * Two requests can be in flight when the listener pauses twice, and the network does not keep
 * their order. Each request takes a number; an answer whose number is not the latest is
 * dropped. Without this, a slow answer for «siz» landing after the one for «sizzla» would
 * replace the right results with the wrong ones — and nothing would look broken.
 *
 * ## What counts as «the same query»
 *
 * `normalizeText` from `@fdm/shared`, the function the server searches with. A trailing space,
 * a capital letter or a comma changes nothing the server would find, so it sends nothing; a
 * query that normalises to nothing — punctuation, an emoji — is an empty field, with no
 * request at all.
 */

import { normalizeText } from '@fdm/shared';

import type { ApiFailure, ApiResult } from './api';

/** Quiet time after the last keystroke before the request goes. */
export const SEARCH_DEBOUNCE_MS = 250;

export interface SearchAsYouTypeHandlers<T> {
  readonly fetch: (text: string) => Promise<ApiResult<T>>;
  /** The normalised query whose answer is drawn — `undefined` while none is, or it failed. */
  readonly answered: () => string | undefined;
  readonly onLoading: () => void;
  /** `text` is the field as it was asked, `query` its normalised form. */
  readonly onAnswer: (data: T, text: string, query: string) => void;
  readonly onFailure: (failure: ApiFailure) => void;
}

export interface SearchAsYouType {
  /**
   * The field now holds `text`: a request after a pause, unless the same query is answered or on
   * its way. Returns the normalised query — `''` when there is nothing to search, and then nothing
   * is scheduled and nothing in flight will be drawn.
   */
  readonly type: (text: string) => string;
  /** Asks for `text` again, now. */
  readonly retry: (text: string) => void;
  /** Nothing scheduled, and whatever is in flight is dropped when it lands. */
  readonly abandon: () => void;
}

export function searchAsYouType<T>(handlers: SearchAsYouTypeHandlers<T>): SearchAsYouType {
  let timer: ReturnType<typeof setTimeout> | undefined;
  /** The number of the newest request. An answer carrying another number is stale. */
  let latest = 0;
  /** The normalised query of the request in flight, if any. */
  let pendingFor = '';

  function cancelTimer(): void {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  }

  function abandon(): void {
    cancelTimer();
    latest += 1;
    pendingFor = '';
  }

  async function run(text: string, query: string): Promise<void> {
    latest += 1;
    const mine = latest;
    pendingFor = query;
    handlers.onLoading();
    const result = await handlers.fetch(text);
    if (mine !== latest) return;
    pendingFor = '';
    if (result.ok) handlers.onAnswer(result.data, text, query);
    else handlers.onFailure(result.error);
  }

  return {
    type: (text) => {
      const query = normalizeText(text);
      if (query === '') {
        abandon();
        return '';
      }
      // The same query as the one answered or on its way: nothing the server would find
      // differently. A scheduled request for another query is dropped with it.
      cancelTimer();
      const settled = handlers.answered() === query && pendingFor === '';
      if (settled || query === pendingFor) return query;
      timer = setTimeout(() => {
        timer = undefined;
        void run(text, query);
      }, SEARCH_DEBOUNCE_MS);
      return query;
    },

    retry: (text) => {
      const query = normalizeText(text);
      if (query === '') return;
      cancelTimer();
      void run(text, query);
    },

    abandon,
  };
}
